import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, Plus, Search, Edit, Trash2, Eye, X,
  Briefcase, DollarSign, Phone, Mail, Hash, Award
} from 'lucide-react';

interface Employee {
  id: number;
  name: string;
  code?: string;
  phone?: string;
  title?: string;
  basicSalary?: number;
  departmentId?: number;
  companyId?: number;
  statusId?: number;
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', phone: '', title: '',
    basicSalary: '', departmentId: '', companyId: '', statusId: '1'
  });

  useEffect(() => { loadEmployees(); }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/employees?pageSize=100');
      if (response.data.success) {
        const data = response.data.data;
        setEmployees(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingEmployee(null);
    setForm({
      name: '', code: '', phone: '', title: '',
      basicSalary: '', departmentId: '', companyId: '', statusId: '1'
    });
    setShowModal(true);
  };

  const openEditModal = (emp: Employee) => {
    setEditingEmployee(emp);
    setForm({
      name: emp.name || '',
      code: emp.code || '',
      phone: emp.phone || '',
      title: emp.title || '',
      basicSalary: emp.basicSalary ? emp.basicSalary.toString() : '',
      departmentId: emp.departmentId ? emp.departmentId.toString() : '',
      companyId: emp.companyId ? emp.companyId.toString() : '',
      statusId: emp.statusId ? emp.statusId.toString() : '1'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        code: form.code || null,
        phone: form.phone || null,
        title: form.title || null,
        basicSalary: form.basicSalary ? parseFloat(form.basicSalary) : null,
        departmentId: form.departmentId ? parseInt(form.departmentId) : null,
        companyId: form.companyId ? parseInt(form.companyId) : null,
        statusId: form.statusId ? parseInt(form.statusId) : 1
      };
      if (editingEmployee) {
        await api.put('/api/employees/' + editingEmployee.id, payload);
        alert('تم تحديث بيانات الموظف بنجاح');
      } else {
        await api.post('/api/employees', payload);
        alert('تم إضافة الموظف بنجاح');
      }
      setShowModal(false);
      loadEmployees();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ بيانات الموظف');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموظف؟')) return;
    try {
      await api.delete('/api/employees/' + id);
      loadEmployees();
      alert('تم حذف الموظف بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف الموظف');
    }
  };

  const filteredEmployees = Array.isArray(employees) ? employees.filter(e =>
    (e.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.code || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.phone || '').includes(search) ||
    (e.title || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: employees.length,
    totalSalary: employees.reduce((sum, e) => sum + (e.basicSalary || 0), 0),
    avgSalary: employees.length > 0 
      ? (employees.reduce((sum, e) => sum + (e.basicSalary || 0), 0) / employees.length).toFixed(0)
      : 0,
    withSalary: employees.filter(e => e.basicSalary && e.basicSalary > 0).length
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  const getStatusLabel = (statusId?: number) => {
    switch (statusId) {
      case 1: return 'نشط';
      case 2: return 'غير نشط';
      case 3: return 'موقوف';
      default: return 'نشط';
    }
  };

  const getStatusStyle = (statusId?: number) => {
    switch (statusId) {
      case 1: return 'bg-green-50 text-green-700';
      case 2: return 'bg-red-50 text-red-700';
      case 3: return 'bg-yellow-50 text-yellow-700';
      default: return 'bg-green-50 text-green-700';
    }
  };

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <Briefcase className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة الموظفين</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة بيانات الموظفين والرواتب</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة موظف جديد</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Users className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي الموظفين</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <DollarSign className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي الرواتب</p>
            <p className='text-2xl font-bold text-green-700'>{stats.totalSalary.toLocaleString()} ج.م</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <Award className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>متوسط الراتب</p>
            <p className='text-2xl font-bold text-blue-700'>{Number(stats.avgSalary).toLocaleString()} ج.م</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Briefcase className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>موظفون براتب</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.withSalary}</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث بالاسم، الكود، الهاتف، أو المسمى الوظيفي...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل الموظفين...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className='p-12 text-center'>
              <Briefcase className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا يوجد موظفون</h3>
              <p className='text-gray-500'>ابدأ بإضافة موظفك الأول</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الموظف</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الكود</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الهاتف</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المسمى الوظيفي</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الراتب</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold'>
                            {(emp.name || 'E').charAt(0)}
                          </div>
                          <p className='font-semibold text-gray-900'>{emp.name}</p>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-mono text-gray-700'>{emp.code || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-2 text-sm text-gray-700'>
                          <Phone className='w-3.5 h-3.5 text-gray-400' />
                          <span>{emp.phone || '-'}</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{emp.title || '-'}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-bold text-green-600'>
                          {emp.basicSalary ? (emp.basicSalary.toLocaleString() + ' ج.م') : '-'}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + getStatusStyle(emp.statusId)}>
                          {getStatusLabel(emp.statusId)}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedEmployee(emp); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(emp)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(emp.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingEmployee ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>الاسم الكامل *</Label>
                    <Input
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='أدخل اسم الموظف'
                    />
                  </div>
                  <div>
                    <Label>كود الموظف</Label>
                    <Input
                      value={form.code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, code: e.target.value })}
                      className={inputClass}
                      placeholder='EMP001'
                    />
                  </div>
                  <div>
                    <Label>رقم الهاتف</Label>
                    <Input
                      value={form.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, phone: e.target.value })}
                      className={inputClass}
                      placeholder='01xxxxxxxxx'
                    />
                  </div>
                  <div>
                    <Label>المسمى الوظيفي</Label>
                    <Input
                      value={form.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                      className={inputClass}
                      placeholder='محاسب، مبرمج، مدير...'
                    />
                  </div>
                  <div>
                    <Label>الراتب الأساسي (ج.م)</Label>
                    <Input
                      type='number'
                      step='0.01'
                      value={form.basicSalary}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, basicSalary: e.target.value })}
                      className={inputClass}
                      placeholder='0.00'
                    />
                  </div>
                  <div>
                    <Label>الحالة</Label>
                    <select
                      value={form.statusId}
                      onChange={(e) => setForm({ ...form, statusId: e.target.value })}
                      className={inputClass}
                    >
                      <option value='1'>نشط</option>
                      <option value='2'>غير نشط</option>
                      <option value='3'>موقوف</option>
                    </select>
                  </div>
                  <div>
                    <Label>رقم القسم</Label>
                    <Input
                      type='number'
                      value={form.departmentId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, departmentId: e.target.value })}
                      className={inputClass}
                      placeholder='رقم القسم (اختياري)'
                    />
                  </div>
                  <div>
                    <Label>رقم الشركة</Label>
                    <Input
                      type='number'
                      value={form.companyId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, companyId: e.target.value })}
                      className={inputClass}
                      placeholder='رقم الشركة (اختياري)'
                    />
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingEmployee ? 'تحديث البيانات' : 'إضافة الموظف'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedEmployee && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل الموظف</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-2xl font-bold'>
                    {(selectedEmployee.name || 'E').charAt(0)}
                  </div>
                  <div>
                    <h3 className='text-xl font-bold'>{selectedEmployee.name}</h3>
                    <p className='text-sm text-gray-500'>{selectedEmployee.title || 'بدون مسمى وظيفي'}</p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_كود الموظف</p>
                    <p className='font-semibold font-mono'>{selectedEmployee.code || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_رقم الهاتف</p>
                    <p className='font-semibold'>{selectedEmployee.phone || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المسمى الوظيفي</p>
                    <p className='font-semibold'>{selectedEmployee.title || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الراتب الأساسي (ج.م)</p>
                    <p className='font-semibold text-green-600 text-xl'>
                      {selectedEmployee.basicSalary ? (selectedEmployee.basicSalary.toLocaleString() + ' ج.م') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الحالة</p>
                    <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + getStatusStyle(selectedEmployee.statusId)}>
                      {getStatusLabel(selectedEmployee.statusId)}
                    </span>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_رقم القسم_ID</p>
                    <p className='font-semibold'>{selectedEmployee.departmentId || '-'}</p>
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedEmployee); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل البيانات
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
